/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, Paper } from "@mui/material";
import { cardsContainerStyles, CardFormula } from "./styles";
import {
  StatisticsTitle,
  StatisticsValue,
  CardContainer,
  IconContainer,
  InformationContainer,
  TitleContainer,
  LightTooltip,
  StyledInfoIcon,
} from "../statistics_cards/styles";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import TableRestaurantOutlinedIcon from '@mui/icons-material/TableRestaurantOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import complianceImage from "../../assets/images/PieChart.svg";
import { StatisticSkeletalLoader } from "../statistic_skeletal_loader";
import { formatWholeNumber } from "../../utils/numberFormatter";
import { formatPercent } from "../../utils/perqReportDrilldown";
import { getUniformRowCounts, getCardLayoutStyles } from "../../utils/statCardGridLayout";

const formatCount = (value) => formatWholeNumber(value ?? 0);

const ICON_COLOR = "#f97316";

const CARDS = [
  {
    key: "restaurant_count",
    icon: <TableRestaurantOutlinedIcon sx={{ color: ICON_COLOR }} />,
    title: "Restaurants",
    description: "Restaurants covered by the selected filters",
    formatValue: formatCount,
  },
  {
    key: "total_orders",
    icon: <ReceiptLongOutlinedIcon sx={{ color: ICON_COLOR }} />,
    title: "Total Orders",
    description: "All orders in the period, excluding investor orders",
    formatValue: formatCount,
  },
  {
    key: "eligible_orders",
    icon: <ReceiptLongOutlinedIcon sx={{ color: ICON_COLOR }} />,
    title: "Eligible Orders",
    description: "Orders with at least one managed perq applied",
    formatValue: formatCount,
  },
  {
    key: "total_check_ins",
    icon: <CheckCircleOutlineOutlinedIcon sx={{ color: ICON_COLOR }} />,
    title: "Total Check-Ins",
    description: "Check-ins recorded at these restaurants, excluding industry cards",
    formatValue: formatCount,
  },
  {
    key: "reconciled_check_ins",
    icon: <HowToRegOutlinedIcon sx={{ color: ICON_COLOR }} />,
    title: "Reconciled Check-Ins",
    description: "Check-ins linked to an eligible order",
    formatValue: formatCount,
  },
  {
    key: "check_in_gap",
    icon: <ErrorOutlineOutlinedIcon sx={{ color: ICON_COLOR }} />,
    title: "Check-In Gap",
    description: "Eligible orders without a reconciled check-in",
    formula: "Eligible Orders - Reconciled Check-Ins",
    formatValue: formatCount,
  },
  {
    key: "compliance_percent",
    icon: <img src={complianceImage} alt="Approx. Check-In Compliance" />,
    title: "Approx. Check-In Compliance",
    description: "Reconciled check-ins as a share of eligible orders",
    formula: "(Reconciled Check-Ins / Eligible Orders) * 100",
    formatValue: formatPercent,
  },
];

// Uniform one-column cards: 4 on top and 3 below on wide screens, pairs on narrow ones.
const NARROW_COLUMN_COUNT = 2;
const WIDE_COLUMN_COUNT = Math.ceil(CARDS.length / 2);
const NARROW_ROW_COUNTS = getUniformRowCounts(CARDS.length, NARROW_COLUMN_COUNT);
const WIDE_ROW_COUNTS = getUniformRowCounts(CARDS.length, WIDE_COLUMN_COUNT);

export const CheckInComplianceCards = ({ data, loading = false, isMobile }) => {
  return (
    <Box
      sx={(theme) => cardsContainerStyles(theme, NARROW_COLUMN_COUNT, WIDE_COLUMN_COUNT)}
      data-testid="check-in-compliance-cards"
    >
      {CARDS.map((card, index) => (
        <Paper
          key={card.key}
          data-testid={`check-in-compliance-card-${card.key}`}
          elevation={0}
          square
          sx={(theme) => ({
            minHeight: 73,
            backgroundColor: "white",
            padding: isMobile ? "12px" : "5px 20px",
            ...getCardLayoutStyles({ index, rowCounts: NARROW_ROW_COUNTS, includeOuterEdges: true }),
            [theme.breakpoints.up(1000)]: {
              ...getCardLayoutStyles({ index, rowCounts: WIDE_ROW_COUNTS, includeOuterEdges: true }),
            },
          })}
        >
          <CardContainer isMobile={isMobile}>
            <IconContainer>
              {card.icon}
            </IconContainer>
            <InformationContainer isMobile={isMobile}>
              <TitleContainer>
                <StatisticsTitle>{card.title}</StatisticsTitle>
                <LightTooltip
                  placement="top"
                  title={
                    card.formula ? (
                      <>
                        {card.description}
                        <CardFormula>{card.formula}</CardFormula>
                      </>
                    ) : (
                      card.description
                    )
                  }
                >
                  <StyledInfoIcon />
                </LightTooltip>
              </TitleContainer>
              {loading ? (
                <StatisticSkeletalLoader />
              ) : (
                <StatisticsValue data-testid={`check-in-compliance-value-${card.key}`}>
                  {data ? card.formatValue(data[card.key]) : "-"}
                </StatisticsValue>
              )}
            </InformationContainer>
          </CardContainer>
        </Paper>
      ))}
    </Box>
  );
};
